import { useForm } from "react-hook-form";
import { ReactElement, useCallback, useEffect, } from "react";

import { format, parse, isValid } from "date-fns";
import { z } from 'zod';

import { Botao, Cores, Corpo, Icone, InputData, InputNumerico, Textfield } from "@componentes-mu/componentes-mu"
import { zodResolver } from '@hookform/resolvers/zod';

import styles from "./styles.module.scss";
import { ColumnSort, Ticket, TicketRequest, TicketResponse } from "../../types/tickets/types";
import { OPEN_TICKET_STATUS_CODES, TicketStatus } from "../../constants/ticketStatus";
import Table from "../../components/Table";
import { Column, SortDirection } from "../../components/Table/types";
import { FlexColumn, FlexRow } from "../../components/Flex";
import { StatusGroupOptions, StatusOptionsProps } from "./StatusGroupOptions";
import { Action, Actions } from "./Actions";
import { Select, SelectOption } from "../../components/Select";
import { Contact } from "../../types/cliente/contatos/types";

export enum TicketTabId {
  ATENDIMENTOS = "atendimentos",
  DESENVOLVIMENTO = "desenvolvimento",
  ORDEM_SERVICO = "ordem-servico",
  HISTORICO_TRANSACAO = "historico-transacao",
}

export type MeusTicketsProps = {
  data: TicketResponse
  filters: TicketRequest
  tabId: TicketTabId
  contacts: Contact[]
  onSelectedStatus: (data: TicketRequest) => void
  onPageChange: (page: number) => void,
  onItemsPerPageChange: (itemsPerPage: number) => void,
  onFilter: (data: TicketRequest) => void
  onAction: (type: Action, ticket: Ticket, event?: React.MouseEvent<HTMLButtonElement>) => void;
  onSort: (conatiner: string, direction: SortDirection) => void
  onExportClick: () => void
  onPriorityClick: () => void
  onSelectViewAttachments: (data: Ticket) => void
}

const schema = z.object({
  numeroTicket: z.number().optional(),
  situacao: z.string().optional(),
  situacaoEspecifica: z.string().optional(),
  oidCliente: z.number().optional(),
  isMaster: z.boolean(),
  pagina: z.number().optional(),
  tamanhoPagina: z.number().optional(),
  campoOrdenado: z.nativeEnum(ColumnSort).optional(),
  sentidoOrdenacao: z.nativeEnum(SortDirection).optional(),
  dataInicial: z.string().optional(),
  dataFinal: z.string().optional(),
  solicitante: z.number().optional(),
  titulo: z.string().optional(),
}).refine((data) => {
  if (data.dataInicial && data.dataFinal) {
    const parsedDataInicial = parse(data.dataInicial, 'dd/MM/yyyy', new Date());
    const parsedDataFinal = parse(data.dataFinal, 'dd/MM/yyyy', new Date());
    return isValid(parsedDataInicial) && isValid(parsedDataFinal) && parsedDataFinal >= parsedDataInicial;
  }
  return true;
}, {
  message: "Data final deve ser maior que a data inicial",
  path: ["dataFinal"],
});

export const MeusTickets = ({ data, filters, tabId, contacts, onSelectedStatus, onPageChange, onItemsPerPageChange,
  onFilter, onAction, onSort, onExportClick, onPriorityClick, onSelectViewAttachments }: MeusTicketsProps) => {


  const { getValues, setValue, watch, handleSubmit, reset, formState: { errors } } = useForm<TicketRequest>({
    defaultValues: {
      ...filters
    },
    resolver: zodResolver(schema),
  });

  const getContactsOptions = (contacts: Contact[]): SelectOption[] => [
    { value: '', label: 'Selecione...' },
    ...contacts.map((contact) => ({
      value: contact.oid,
      label: contact.nome,
    })),
  ];

  useEffect(() => {
    if (filters) {
      reset(filters);
    }
  }, [filters, reset]);

  const contactsOptions = getContactsOptions(contacts)

  const situacaoEspecificaOptions: SelectOption[] = [
    { value: '', label: 'Todas' },
    ...OPEN_TICKET_STATUS_CODES
      .map((code) => ({ value: code, label: TicketStatus[code] ?? code }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
  ];

  const statusOptions: StatusOptionsProps[] = [
    {
      value: 'todos',
      description: 'Todos',
      icon: 'comment',
      onClick: () => {
        setValue('situacaoEspecifica', undefined);
        onSelectedStatus({
          ...getValues(),
          situacao: 'todos',
          situacaoEspecifica: undefined,
        });
      },
    },
    {
      value: 'em aberto',
      description: 'Em Aberto',
      icon: 'published_with_changes',
      onClick: () => onSelectedStatus({
        ...getValues(),
        situacao: 'em aberto'
      }),
    },
    {
      value: 'fechados',
      description: 'Fechados',
      icon: 'check_circle',
      onClick: () => {
        setValue('situacaoEspecifica', undefined);
        onSelectedStatus({
          ...getValues(),
          situacao: 'fechados',
          situacaoEspecifica: undefined,
        });
      },
    },
  ]

  const renderContainer = useCallback((conatiner: string, original: Ticket) => {
    const conatinerObj: Record<string, ReactElement> = {
      prioridadeCliente: (
        <>{original.prioridadeCliente || '-'}</>
      ),
      inclusao: (
        <>{format(new Date(original.inclusao), 'dd/MM/yyyy')}</>
      ),
      titulo: (
        <div style={{ paddingTop: '10px', paddingBottom: '10px' }}>
          <span
            style={{ whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ __html: original.titulo }}
          />
        </div>
      ),
      status: (
        <>{TicketStatus[original.status?.toUpperCase() as keyof typeof TicketStatus] || ""}</>
      ),
      canalDiretoAplicado: (
        <>
          {original.canalDiretoRespondido
            ? 'Respondido'
            : original.canalDiretoAplicado
              ? 'Aplicado'
              : '-'}
        </>
      ),
      possuiAnexo: (
        <>
          {original.possuiAnexo ? (
            <button
              type="button"
              onClick={() => onSelectViewAttachments(original)}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 0
              }}
            >
              <Icone
                nome="image"
                cor={Cores.CoresIdentidadeMU.azulMu}
              />
            </button>
          ) : '-'}
        </>
      ),
      actions: (
        <Actions ticket={original}
          userIsMaster={filters.isMaster}
          onAction={onAction}
        />
      ),
    }

    return conatinerObj[conatiner]
  }, [filters.isMaster, onAction, onSelectViewAttachments]);

  const columns: Column<Ticket>[] = [
    ...(filters.isMaster
      ? [{
        key: "prioridadeCliente",
        header: "PRIORIDADE",
        Cell: (row) => renderContainer('prioridadeCliente', row),
        align: "center",
        sortable: true,
        onSort: (_, sort) => onSort('PRIORIDADE_CLIENTE', sort),
        sortDirection: filters.campoOrdenado == ColumnSort.PRIORIDADE_CLIENTE ? filters.sentidoOrdenacao : undefined,
      } satisfies Column<Ticket>]
      : []),
    {
      key: "ticket",
      header: "Nº TICKET",
      width: '10%',
      align: "center",
      sortable: true,
      onSort: onSort,
      sortDirection: filters.campoOrdenado == ColumnSort.TICKET ? filters.sentidoOrdenacao : undefined
    },
    {
      key: "inclusao",
      header: "INCLUSÃO",
      width: '10%',
      Cell: (row) => renderContainer('inclusao', row),
      align: "center",
      sortable: true,
      onSort: (_, sort) => onSort('date', sort),
      sortDirection: filters.campoOrdenado == ColumnSort.DATE ? filters.sentidoOrdenacao : undefined,

    },
    {
      key: "titulo",
      header: "TÍTULO",
      width: filters.isMaster ? '30%' : '40%',
      Cell: (row) => renderContainer('titulo', row),
      align: "left",
    },
    {
      key: "tipo",
      header: "TIPO",
      width: '10%',
      align: "center",
    },
    {
      key: "solicitante",
      header: "SOLICITANTE",
      width: '15%',
      align: "center",
      sortable: true,
      onSort: onSort,
      sortDirection: filters.campoOrdenado == ColumnSort.SOLICITANTE ? filters.sentidoOrdenacao : undefined,
    },
    {
      key: "status",
      header: "STATUS",
      Cell: (row) => renderContainer('status', row),
      width: '10%',
      align: "center",
      sortable: true,
      onSort: onSort,
      sortDirection: filters.campoOrdenado == ColumnSort.STATUS ? filters.sentidoOrdenacao : undefined,
    },
    {
      key: "possuiAnexo",
      header: "POSSUI ANEXO",
      Cell: (row) => renderContainer('possuiAnexo', row),
      width: '10%',
      align: "center",
    },
    ...(filters.isMaster
      ? [{
        key: "canalDiretoAplicado",
        header: "CANAL DIRETO",
        Cell: (row) => renderContainer('canalDiretoAplicado', row),
        width: '10%',
        align: "center",
        sortable: true,
        onSort: (_, sort) => onSort('CANAL_DIRETO', sort),
        sortDirection: filters.campoOrdenado == ColumnSort.CANAL_DIRETO ? filters.sentidoOrdenacao : undefined,
      } satisfies Column<Ticket>]
      : []),
    {
      key: "actions",
      header: "AÇÔES",
      Cell: (row) => renderContainer('actions', row),
      align: "center",
    }
  ];



  return (
    <>

      <form className={styles.formContainer} onSubmit={handleSubmit(onFilter)}>
        <FlexColumn gap='1rem'>

          <FlexRow alignItems='center' gap='1rem' justifyContent='center'>

            <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600}
              style={{ width: 100, textAlign: 'right' }} negrito={500}>
              Solicitante
            </Corpo>

            <div style={{ flex: 1 }}>
              <Select
                value={watch('solicitante')}
                onChange={oid => setValue("solicitante", oid ? Number(oid) : undefined)}
                options={contactsOptions}
              />
            </div>

            {filters.situacao === 'em aberto' && (
              <>
                <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600}
                  style={{ width: 100, textAlign: 'right' }} negrito={500}>
                  Situação
                </Corpo>

                <div style={{ flex: 1 }}>
                  <Select
                    value={watch('situacaoEspecifica') ?? ''}
                    onChange={value => setValue('situacaoEspecifica', value ? String(value) : undefined)}
                    options={situacaoEspecificaOptions}
                  />
                </div>
              </>
            )}

            <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600}
              style={{ width: 100, textAlign: 'right' }} negrito={500}>
              Inclusão
            </Corpo>

            <div style={{ maxWidth: "200px" }}>
              <InputData
                id='dataInicial'
                onChange={(e) => setValue("dataInicial", e || undefined)}
                label={false}
                placeholder={true}
                formatoData="dd/mm/aaaa"
                value={watch('dataInicial')}
              />
            </div>

            <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600} negrito={500}>até</Corpo>

            <div style={{ maxWidth: "200px" }}>
              <InputData
                id='dataFinal'
                onChange={(e) => setValue("dataFinal", e || undefined)}
                label={false}
                placeholder={true}
                formatoData="dd/mm/aaaa"
                value={watch('dataFinal')}
                variante={errors.dataFinal ? 'error' : 'regular'}
              />
            </div>

          </FlexRow>

          <FlexRow alignItems='center' gap='4rem'>

            {filters.situacao && <StatusGroupOptions options={statusOptions} value={filters.situacao} />}


            <FlexColumn alignItems='center' gap='1rem'>

              <FlexRow alignItems='center' gap='1rem'>

                <div style={{ width: 120 }}>
                  <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600} negrito={500}>N° do Ticket</Corpo>
                </div>

                <div style={{ flex: 1 }}>
                  <InputNumerico
                    id='nomeTreinamento'
                    placeholder={"XXXXXX"}
                    valor={watch('numeroTicket')}
                    onChange={(e) => setValue("numeroTicket", e || undefined)}
                    casasDecimais={0}
                    casasInteiras={10}
                  />
                </div>

              </FlexRow>

              <FlexRow alignItems='center' gap='1rem'>

                <div style={{ width: 120 }}>
                  <Corpo tipo="body2" cor={Cores.GradacaoNeutros.CinzaFillMU600} negrito={500}>Titulo</Corpo>
                </div>

                <Textfield
                  iconeInputField=""
                  titulo=""
                  style={{ flex: 1 }}
                  id="Titulo"
                  placeholder="Descrição"
                  tamanho="medium"
                  tipoDeEntrada='text'
                  onChange={(e) => setValue("titulo", e || '')}
                  valor={watch('titulo')}
                  variante={errors.titulo ? 'error' : 'regular'}
                  conteudoDeAjuda={{
                    texto: errors.titulo?.message || '',
                    estado: errors.titulo ? 'error' : 'regular',
                  }}
                />

              </FlexRow>


            </FlexColumn>

            <Botao tipoComportamento="submit" cor={Cores.GradacaoNeutros.CinzaFillMU100}
              tipo='tipoDefault'
              style={{ minWidth: '100px' }}
            >
              <Corpo tipo="button1" cor={Cores.CoresIdentidadeMU.azulMu} negrito={700}>
                Buscar
              </Corpo>
            </Botao>
          </FlexRow>
        </FlexColumn >

      </form >


      <Table
        columns={columns}
        data={data.tickets}
        currentPage={data.paginaAtual}
        itemsPerPage={data.tamanhoPagina}
        totalItems={data.totalItens}
        showExportButton
        showPriorityButton={filters.isMaster && tabId == TicketTabId.ATENDIMENTOS}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
        onExportClick={onExportClick}
        onPriorityClick={onPriorityClick}
      />
    </>
  )
}